import { useEffect, useRef, useState } from 'react';
import { IconClose, IconArrowLeft, IconArrowRight, IconDownload } from '../lib/icons.jsx';

/**
 * Full-screen photo viewer. Keyboard arrows on desktop, swipe on a phone.
 * Shows the preview tier (fast, already warm from the grid); the download
 * button always fetches the untouched original.
 */
export default function Lightbox({ photos, index, onClose, onIndex, allowDownload, onDownload }) {
  const photo = photos[index];
  const touch = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  // Which way we moved, so the next photo slides in from the side you're
  // heading towards. Arrows, swipes and the keyboard all just change the index,
  // so deriving it here keeps them all feeling the same.
  //
  // Held in refs and updated during render on purpose: the direction has to
  // survive the re-renders that follow (loading state, StrictMode's double
  // invoke). An effect would run too late and a plain const would reset to ''
  // on the very next render, stripping the class before the animation ran.
  const prevIndex = useRef(index);
  const dirRef = useRef('');
  if (index !== prevIndex.current) {
    dirRef.current = index > prevIndex.current ? 'next' : 'prev';
    prevIndex.current = index;
  }
  const direction = dirRef.current;

  useEffect(() => setLoaded(false), [index]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onIndex(Math.min(index + 1, photos.length - 1));
      if (e.key === 'ArrowLeft') onIndex(Math.max(index - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [index, photos.length, onClose, onIndex]);

  // Preload the neighbours so next/prev feels instant.
  useEffect(() => {
    [index - 1, index + 1].forEach((i) => {
      if (photos[i]) new Image().src = photos[i].src;
    });
  }, [index, photos]);

  if (!photo) return null;

  return (
    <div
      className="lightbox"
      onTouchStart={(e) => {
        touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }}
      onTouchEnd={(e) => {
        if (!touch.current) return;
        const dx = e.changedTouches[0].clientX - touch.current.x;
        const dy = e.changedTouches[0].clientY - touch.current.y;
        if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
          onIndex(dx < 0 ? Math.min(index + 1, photos.length - 1) : Math.max(index - 1, 0));
        } else if (dy > 90) {
          onClose();
        }
        touch.current = null;
      }}
    >
      <div className="lightbox-bar">
        <span className="label on-dark">
          {index + 1} / {photos.length}
        </span>
        <button className="icon-only" onClick={onClose} aria-label="Close">
          <IconClose />
        </button>
      </div>

      <div className="lightbox-stage">
        {/* Keyed on the index so the wrapper remounts each move and replays the
            slide; the image keeps its own fade for when the file lands. */}
        <div key={index} className={`lb-slide ${direction ? `from-${direction}` : ''}`}>
          <img
            src={photo.src}
            alt={photo.filename}
            className={loaded ? 'loaded' : ''}
            onLoad={() => setLoaded(true)}
          />
        </div>
        {index > 0 && (
          <button className="lightbox-nav prev" onClick={() => onIndex(index - 1)} aria-label="Previous photo">
            <IconArrowLeft />
          </button>
        )}
        {index < photos.length - 1 && (
          <button className="lightbox-nav next" onClick={() => onIndex(index + 1)} aria-label="Next photo">
            <IconArrowRight />
          </button>
        )}
      </div>

      <div className="lightbox-bar">
        <span className="tiny" style={{ color: 'rgba(255,255,255,0.55)' }}>
          {photo.filename}
        </span>
        {allowDownload && (
          <button
            disabled={saving}
            onClick={async () => {
              // On a phone the original is fetched in full before the share
              // sheet can open, which is a beat or two on mobile data. Without
              // this the button looks dead and gets tapped again.
              setSaving(true);
              try {
                await onDownload(photo);
              } finally {
                setSaving(false);
              }
            }}
          >
            <IconDownload width={15} height={15} />
            {saving ? 'Preparing…' : 'Download original'}
          </button>
        )}
      </div>
    </div>
  );
}
